import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Upload, Plus, Trash2, FileSpreadsheet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const CONF = (c) =>
  c >= 0.9 ? { label: "High", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" }
  : c >= 0.7 ? { label: "Medium", cls: "bg-amber-50 text-amber-700 border-amber-200" }
  : c > 0 ? { label: "Low", cls: "bg-orange-50 text-orange-700 border-orange-200" }
  : { label: "None", cls: "bg-gray-100 dark:bg-neutral-800 text-gray-500 dark:text-neutral-400 border-gray-200 dark:border-neutral-700" };

/** Upload → review → apply. Nothing is written until Apply is clicked. */
export default function ScheduleUploadDialog({ projectId, open, onOpenChange, onApplied }) {
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);   // { staging_id, activities, scopes, ... }
  const [rows, setRows] = useState([]);         // editable review rows
  const [scheduleDate, setScheduleDate] = useState("");
  const [applying, setApplying] = useState(false);

  const reset = () => { setParsed(null); setRows([]); setScheduleDate(""); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/projects/${projectId}/schedule/parse`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParsed(data);
      setScheduleDate(data.schedule_date_guess || "");
      setRows((data.activities || []).map((a) => ({
        uid: `r${a.row}`,
        activity_name: a.activity_name,
        start: a.start, finish: a.finish, duration_days: a.duration_days,
        scope_type: a.proposal?.scope_type || "",
        scope_code: a.proposal?.scope_code || "",
        confidence: a.proposal?.confidence || 0,
        reason: a.proposal?.reason || "",
        building: "", floor: "", area: "",
      })));
      toast.success(`Parsed ${data.activities.length} activities from ${data.file_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not parse that schedule");
    } finally { setParsing(false); }
  };

  const setRow = (uid, patch) => setRows((cur) => cur.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));
  const duplicateRow = (uid) => setRows((cur) => {
    const i = cur.findIndex((r) => r.uid === uid);
    const src = cur[i];
    const copy = { ...src, uid: `${src.uid}-c${Date.now()}`, scope_type: "", scope_code: "", confidence: 0, reason: "manual" };
    return [...cur.slice(0, i + 1), copy, ...cur.slice(i + 1)];
  });
  const removeRow = (uid) => setRows((cur) => cur.filter((r) => r.uid !== uid));

  const mapped = rows.filter((r) => r.scope_type);

  const apply = async () => {
    if (!scheduleDate) { toast.error("Enter the schedule date (the GC's data date)"); return; }
    setApplying(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/schedule/apply`, {
        staging_id: parsed.staging_id,
        schedule_date: scheduleDate,
        mappings: mapped.map((r) => ({
          activity_name: r.activity_name, scope_type: r.scope_type, scope_code: r.scope_code,
          building: r.building, floor: r.floor, area: r.area,
          start: r.start, finish: r.finish, duration_days: r.duration_days,
          confidence: r.confidence,
        })),
      });
      toast.success(`Schedule applied — ${data.mappings.length} scope rows logged`);
      reset();
      onOpenChange(false);
      onApplied?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Apply failed");
    } finally { setApplying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-6xl" data-testid="schedule-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload GC Schedule</DialogTitle>
        </DialogHeader>

        {!parsed ? (
          <div className="py-6 text-center space-y-3">
            <FileSpreadsheet className="w-8 h-8 mx-auto text-gray-400" strokeWidth={1.5} />
            <p className="text-sm text-gray-600">
              XLSX, CSV, text-based PDF or MS Project XML. We look for activity name, start, finish and duration.
            </p>
            <p className="text-xs text-gray-400">Primavera XER is not supported yet — export to XLSX or PDF.</p>
            <label className="inline-flex items-center gap-2 px-3 h-9 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm cursor-pointer">
              <Upload className="w-4 h-4" />
              {parsing ? "Parsing…" : "Choose schedule file"}
              <input
                data-testid="schedule-file-input"
                type="file"
                accept=".xlsx,.xlsm,.xls,.csv,.txt,.tsv,.pdf,.xml"
                onChange={onFile}
                className="hidden"
                disabled={parsing}
              />
            </label>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-end gap-4 text-sm">
              <div>
                <Label>Schedule date (GC data date)</Label>
                <Input data-testid="schedule-date-input" type="date" value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)} className="h-8 w-40" />
              </div>
              <div className="text-xs text-gray-500">
                <span className="font-medium text-gray-700">{parsed.file_name}</span> ·{" "}
                {rows.length} activities · <span data-testid="review-mapped-count">{mapped.length}</span> mapped ·{" "}
                {rows.length - mapped.length} ignored
                {parsed.prior_schedule && <> · prior schedule {parsed.prior_schedule.schedule_date}</>}
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-auto max-h-[52vh]">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                    <th className="text-left px-3 py-2 font-medium">GC activity</th>
                    <th className="text-left px-2 py-2 font-medium">Start</th>
                    <th className="text-left px-2 py-2 font-medium">Finish</th>
                    <th className="text-left px-2 py-2 font-medium">Dur</th>
                    <th className="text-left px-2 py-2 font-medium">ABS scope</th>
                    <th className="text-left px-2 py-2 font-medium">Bldg / Floor / Area</th>
                    <th className="text-left px-2 py-2 font-medium">Match</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const conf = CONF(r.confidence);
                    return (
                      <tr key={r.uid} className={`border-t border-gray-100 ${r.scope_type ? "" : "bg-gray-50/60 dark:bg-white/[0.03]"}`} data-testid={`review-row-${r.uid}`}>
                        <td className="px-3 py-1.5 text-gray-800 max-w-[280px] truncate" title={r.activity_name}>{r.activity_name}</td>
                        <td className="px-2 py-1.5 text-xs text-gray-600">{r.start || "—"}</td>
                        <td className="px-2 py-1.5 text-xs text-gray-600">{r.finish || "—"}</td>
                        <td className="px-2 py-1.5 text-xs text-gray-600">{r.duration_days ?? "—"}</td>
                        <td className="px-2 py-1.5">
                          <select
                            data-testid={`review-scope-${r.uid}`}
                            value={r.scope_type}
                            onChange={(e) => {
                              const sc = (parsed.scopes || []).find((s) => s.scope_type === e.target.value);
                              setRow(r.uid, { scope_type: e.target.value, scope_code: sc?.scope_code || "" });
                            }}
                            className="h-7 text-xs border border-gray-300 rounded px-1.5 bg-white min-w-[130px]"
                          >
                            <option value="">Ignore</option>
                            {(parsed.scopes || []).map((s) => (
                              <option key={s.scope_type} value={s.scope_type}>
                                {s.scope_code || s.scope_type}{s.scope_code && s.scope_code !== s.scope_type ? ` · ${s.scope_type}` : ""}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="flex gap-1">
                            <Input data-testid={`review-building-${r.uid}`} value={r.building} placeholder="Bldg"
                              onChange={(e) => setRow(r.uid, { building: e.target.value })} className="h-7 w-16 text-xs" />
                            <Input data-testid={`review-floor-${r.uid}`} value={r.floor} placeholder="Flr"
                              onChange={(e) => setRow(r.uid, { floor: e.target.value })} className="h-7 w-14 text-xs" />
                            <Input data-testid={`review-area-${r.uid}`} value={r.area} placeholder="Area"
                              onChange={(e) => setRow(r.uid, { area: e.target.value })} className="h-7 w-20 text-xs" />
                          </div>
                        </td>
                        <td className="px-2 py-1.5">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-semibold ${conf.cls}`} title={r.reason}>
                            {conf.label}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          <button data-testid={`review-split-${r.uid}`} onClick={() => duplicateRow(r.uid)}
                            title="Map this activity to another scope/area" className="text-gray-400 hover:text-blue-600 p-1">
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                          {r.uid.includes("-c") && (
                            <button data-testid={`review-remove-${r.uid}`} onClick={() => removeRow(r.uid)}
                              title="Remove this extra row" className="text-gray-400 hover:text-red-600 p-1">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400">
              Nothing is saved until you click Apply. Add keyword synonyms in Settings → Schedule Keywords to improve future matches.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} data-testid="schedule-upload-cancel">Cancel</Button>
          {parsed && (
            <Button onClick={apply} disabled={applying} data-testid="schedule-apply-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white">
              {applying ? "Applying…" : mapped.length ? `Apply ${mapped.length} mapping${mapped.length === 1 ? "" : "s"}` : `Store schedule (${rows.length} activities)`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
